/**
 * Foreground in-app banner for new notifications.
 *
 * Subscribes to realtime INSERTs on `public.notifications` for the current
 * user and surfaces a sonner toast with a deep-link action. This is the
 * foreground counterpart to APNs/FCM alert push (which only fires when the
 * app is backgrounded or killed).
 *
 * Mounted once at the portal layout level. Safe to mount multiple times -
 * the channel name is user-scoped and React effect dedupes per render.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, CreditCard, MessageSquare, Shield, Megaphone, Bell } from "lucide-react";
import { createElement } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { AppNotification, NotificationCategory } from "@/hooks/useNotifications";

const ICONS: Record<NotificationCategory, typeof Phone> = {
  call: Phone,
  billing: CreditCard,
  chat: MessageSquare,
  security: Shield,
  promo: Megaphone,
  system: Bell,
};

export function useNotificationBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Suppress duplicate banners (realtime can occasionally re-deliver).
  const seenIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notif-banner:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const n = payload.new as AppNotification;
          if (!n?.id || seenIds.current.has(n.id)) return;
          seenIds.current.add(n.id);

          // Skip stale notifications inserted >30s ago (e.g. reconnect catch-up).
          const ageMs = Date.now() - new Date(n.created_at).getTime();
          if (ageMs > 30_000) return;

          const Icon = ICONS[n.category] ?? Bell;
          toast(n.title, {
            description: n.body ?? undefined,
            icon: createElement(Icon, { className: "h-5 w-5" }),
            duration: 6000,
            action: n.deep_link
              ? {
                  label: "View",
                  onClick: () => navigate(n.deep_link!),
                }
              : undefined,
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, navigate]);
}
